/**
 * RiskAssessmentSection
 * 
 * Search result section for Risk Assessment (EX4)
 * Displays matching objectives grouped by assessment/process
 * Matches exact styling from Tab2ObjectivesAndRisks.tsx
 * 
 * NOTE: This is a display-only component. Data is fetched by useGlobalSearch hook.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Collapse, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import styled from 'styled-components';
import { Objective } from '../../../../services/riskAssessmentService';
import HighlightText from '../components/HighlightText';
import { RiskAssessmentResult } from '../useGlobalSearch';

const { Panel } = Collapse;

// ========================================
// Styled Components - Match Tab2ObjectivesAndRisks.tsx exactly
// ========================================

const SectionContainer = styled.div`
  margin-bottom: 24px;
`;

const SectionHeader = styled.div`
  background: #FEF3C7;
  padding: 12px 16px;
  border-radius: 8px 8px 0 0;
  border: 1px solid #FDE68A;
  border-bottom: none;
`;

const SectionTitle = styled.h3`
  font-family: Inter;
  font-weight: 600;
  font-size: 14px;
  color: #92400E;
  margin: 0;
`;

const ContentContainer = styled.div`
  background: white;
  border: 1px solid #E5E7EB;
  overflow: hidden;
`;

const SubSectionTitle = styled.span`
  font-family: Inter;
  font-weight: 500;
  font-size: 13px;
  color: #374151;
`;

const SubSectionCount = styled.span`
  font-family: Inter;
  font-weight: 400;
  font-size: 13px;
  color: #6B7280;
  margin-left: 8px;
`;

const TableWrapper = styled.div`
  .ant-table {
    border-radius: 0;
  }

  .ant-table-thead > tr > th {
    background: #F8FAFC;
    font-family: Inter;
    font-weight: 500;
    font-size: 10px;
    color: #6B7280;
    padding: 6px 8px;
    border-bottom: 1px solid #E5E7EB;
    border-left: 1px solid #E5E7EB;
    
    &:first-child {
      border-left: none;
    }
    
    /* Remove Ant Design's internal column separators */
    &::before {
      display: none !important;
    }
  }

  .ant-table-tbody > tr > td {
    font-family: Inter;
    font-size: 12px;
    color: #1F2937;
    padding: 12px 8px;
    border-bottom: 1px solid #E5E7EB;
    border-left: 1px solid #E5E7EB;
    vertical-align: top;
    
    &:first-child {
      border-left: none;
    }
  }

  .ant-table-tbody > tr:last-child > td {
    border-bottom: none;
  }

  .ant-table-tbody > tr:hover > td {
    background: #F9FAFB;
  }
`;

const StyledCollapse = styled(Collapse)`
  border: none;
  background: white;

  .ant-collapse-item {
    border-bottom: 1px solid #E5E7EB;

    &:last-child {
      border-bottom: none;
    }
  }

  .ant-collapse-header {
    padding: 12px 16px !important;
    background: #F8FAFC;
  }

  .ant-collapse-content-box {
    padding: 0 !important;
  }
`;

// Cell Components - Match Tab2ObjectivesAndRisks.tsx exactly
const IndexCell = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  padding: 0 12px;
  border: 1px solid #E5E7EB;
  border-radius: 14px;
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  color: #6B7280;
`;

const MultiLineCell = styled.div`
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.5;
  max-height: calc(1.5em * 4);
  word-break: break-word;
`;

// Risk Chip - Match Tab2ObjectivesAndRisks.tsx exactly
const RiskChip = styled.span<{ $risk: string }>`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
  background: white;
  white-space: nowrap;

  ${props => {
    switch (props.$risk) {
      case 'Med':
        return `
          border: 1px solid #FACC15;
          color: #FACC15;
        `;
      case 'Low':
        return `
          border: 1px solid #2DD4BF;
          color: #2DD4BF;
        `;
      case 'High':
        return `
          border: 1px solid #EF4444;
          color: #EF4444;
        `;
      case 'Unranked':
      default:
        return `
          border: 1px solid #60A5FA;
          color: #60A5FA;
        `;
    }
  }}

  &:hover {
    opacity: 0.8;
  }

  &::before {
    content: '●';
    margin-right: 6px;
    font-size: 10px;
  }
`;

// Chip for Financial Statement Assertions - Match Tab2ObjectivesAndRisks.tsx exactly
const AssertionChip = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 12px;
  height: 28px;
  background: #FEF9C3;
  color: #854D0E;
  border-radius: 14px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  flex-shrink: 0;
`;

// Control Chip - Match Tab2ObjectivesAndRisks.tsx exactly
const ControlChip = styled.span<{ $clickable?: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  background: #EFF6FF;
  color: #1E40AF;
  border-radius: 50px;
  font-size: 12px;
  font-weight: 500;
  cursor: ${props => props.$clickable ? 'pointer' : 'default'};
  transition: all 0.2s;
  white-space: nowrap;
  flex-shrink: 0;
  
  &:hover {
    ${props => props.$clickable && `
      background: #DBEAFE;
      transform: translateY(-1px);
      box-shadow: 0 2px 4px rgba(30, 64, 175, 0.15);
    `}
  }
`;

const EmptyCell = styled.span`
  color: #9CA3AF;
  font-size: 14px;
`;

// Tooltip components - Match Tab2ObjectivesAndRisks.tsx exactly
const TooltipTitle = styled.div`
  font-family: Inter;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: #1F2937;
  text-align: center;
  margin-bottom: 12px;
`;

const TooltipList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TooltipItem = styled.div<{ $variant?: 'assertion' | 'control' }>`
  padding: 8px 12px;
  background: ${props => props.$variant === 'control' ? '#EFF6FF' : '#FEF9C3'};
  border-radius: 6px;
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: ${props => props.$variant === 'control' ? '#1E40AF' : '#854D0E'};
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    background: ${props => props.$variant === 'control' ? '#DBEAFE' : '#FDE68A'};
    transform: translateX(2px);
  }
`;

// ========================================
// Types
// ========================================

export interface RiskAssessmentSectionProps {
  data: RiskAssessmentResult;
  searchText: string;
}

// Extended Objective with assessmentId for navigation
interface ObjectiveWithAssessment extends Objective {
  assessmentId?: string;
}

// ========================================
// Component
// ========================================

const RiskAssessmentSection: React.FC<RiskAssessmentSectionProps> = ({
  data,
  searchText,
}) => {
  const navigate = useNavigate();

  // Navigation handlers
  const handleNavigateToControl = (controlId: string) => {
    navigate(`/control-activities/${controlId}`);
  };

  const handleNavigateToAssessment = (assessmentId: string, tab: string = 'tab2') => {
    navigate(`/risk-assessment/${assessmentId}/${tab}`);
  };

  // Table columns - Match Tab2ObjectivesAndRisks.tsx column templates
  const columns: ColumnsType<ObjectiveWithAssessment> = [
    {
      title: '#',
      dataIndex: 'index',
      key: 'index',
      width: 68,
      align: 'center',
      render: (index: number) => (
        <IndexCell>{index.toString().padStart(2, '0')}</IndexCell>
      ),
    },
    {
      title: 'Objectives',
      dataIndex: 'objectives',
      key: 'objectives',
      width: 'auto',
      render: (text: string) => {
        // Strip HTML tags for plain text display
        const plainText = text?.replace(/<[^>]*>/g, '') || '';
        return (
          <MultiLineCell title={plainText}>
            <HighlightText text={plainText} searchText={searchText} />
          </MultiLineCell>
        );
      },
    },
    {
      title: 'Risks',
      dataIndex: 'risks',
      key: 'risks',
      width: 'auto',
      render: (text: string) => (
        <MultiLineCell title={text}>
          <HighlightText text={text || '-'} searchText={searchText} />
        </MultiLineCell>
      ),
    },
    {
      title: 'Risk Mitigating Controls',
      dataIndex: 'riskMitigatingControls',
      key: 'riskMitigatingControls',
      width: 180,
      render: (controls: string[], record: ObjectiveWithAssessment) => {
        if (!controls || controls.length === 0) {
          // Show controlText if available (manual text)
          if (record.controlText && record.controlText.trim() !== '') {
            return <div style={{ color: '#1F2937', whiteSpace: 'pre-wrap' }}>{record.controlText}</div>;
          }
          return <EmptyCell>-</EmptyCell>;
        }

        const controlsContent = (
          <div style={{ display: 'flex', flexWrap: 'nowrap', gap: '4px', overflow: 'hidden' }}>
            {controls.map((control, index) => {
              const controlId = record.controlActivityIds?.[index];
              return (
                <ControlChip
                  key={index}
                  $clickable={!!controlId}
                  onClick={() => controlId && handleNavigateToControl(controlId)}
                  title={controlId ? 'Click to view control details' : undefined}
                >
                  <HighlightText text={control} searchText={searchText} />
                </ControlChip>
              );
            })}
          </div>
        );

        // Show tooltip only if multiple controls
        if (controls.length > 1) {
          const tooltipContent = (
            <div>
              <TooltipTitle>Risk Mitigating Controls</TooltipTitle>
              <TooltipList>
                {controls.map((control, index) => {
                  const controlId = record.controlActivityIds?.[index];
                  return (
                    <TooltipItem
                      key={index}
                      $variant="control"
                      onClick={() => controlId && handleNavigateToControl(controlId)}
                      style={{ cursor: controlId ? 'pointer' : 'default' }}
                    >
                      <HighlightText text={control} searchText={searchText} />
                    </TooltipItem>
                  );
                })}
              </TooltipList>
            </div>
          );

          return (
            <Tooltip
              title={tooltipContent}
              placement="top"
              overlayStyle={{ maxWidth: '400px' }}
              overlayInnerStyle={{
                backgroundColor: '#FFFFFF',
                padding: '16px',
                borderRadius: '12px',
                boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
              }}
              color="#FFFFFF"
            >
              {controlsContent}
            </Tooltip>
          );
        }

        return controlsContent;
      },
    },
    {
      title: 'Control Risk Ranking',
      dataIndex: 'controlRiskRanking',
      key: 'controlRiskRanking',
      width: 160,
      render: (ranking: string, record: ObjectiveWithAssessment) => (
        <RiskChip 
          $risk={ranking}
          onClick={() => handleNavigateToAssessment(record.assessmentId || '', 'tab2')}
        >
          {ranking}
        </RiskChip>
      ),
    },
    {
      title: 'Financial Statement Assertions',
      dataIndex: 'financialStatementAssertions',
      key: 'financialStatementAssertions',
      width: 200,
      render: (assertions: string[]) => {
        if (!assertions || assertions.length === 0) {
          return <EmptyCell>-</EmptyCell>;
        }

        // Tooltip content for multiple assertions
        const tooltipContent = assertions.length > 1 ? (
          <div>
            <TooltipTitle>Financial Statement Assertions</TooltipTitle>
            <TooltipList>
              {assertions.map((assertion, index) => (
                <TooltipItem key={index}>
                  <HighlightText text={assertion} searchText={searchText} />
                </TooltipItem>
              ))}
            </TooltipList>
          </div>
        ) : null;

        const assertionsContent = (
          <div style={{ display: 'flex', flexWrap: 'nowrap', gap: '4px', overflow: 'hidden' }}>
            {assertions.map((assertion, index) => (
              <AssertionChip key={index}>
                <HighlightText text={assertion} searchText={searchText} />
              </AssertionChip>
            ))}
          </div>
        );

        // Show tooltip only if multiple assertions
        if (tooltipContent) {
          return (
            <Tooltip
              title={tooltipContent}
              placement="top"
              overlayStyle={{ maxWidth: '400px' }}
              overlayInnerStyle={{
                backgroundColor: '#FFFFFF',
                padding: '16px',
                borderRadius: '12px',
                boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
              }}
              color="#FFFFFF"
            >
              {assertionsContent}
            </Tooltip>
          );
        }

        return assertionsContent;
      },
    },
  ];

  // Calculate total results
  const totalResults = data.assessments.reduce((sum, a) => sum + a.objectives.length, 0);

  // Don't render if no results
  if (data.assessments.length === 0) {
    return null;
  }

  return (
    <SectionContainer>
      <SectionHeader>
        <SectionTitle>Risk Assessment ({totalResults} results)</SectionTitle>
      </SectionHeader>
      
      <ContentContainer>
        <StyledCollapse defaultActiveKey={data.assessments.map(a => a.assessment.assessmentId)}>
          {data.assessments.map((item) => (
            <Panel
              key={item.assessment.assessmentId}
              header={
                <>
                  <SubSectionTitle>
                    {item.assessment.cycleName} - {item.assessment.processName}
                  </SubSectionTitle>
                  <SubSectionCount>({item.objectives.length})</SubSectionCount>
                </>
              }
            >
              <TableWrapper>
                <Table
                  columns={columns}
                  dataSource={item.objectives.map(obj => ({
                    ...obj,
                    assessmentId: item.assessment.assessmentId,
                  }))}
                  rowKey="id"
                  pagination={false}
                  size="small"
                />
              </TableWrapper>
            </Panel>
          ))}
        </StyledCollapse>
      </ContentContainer>
    </SectionContainer>
  );
};

export default RiskAssessmentSection;
